import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Table,
  Empty,
  Drawer,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  message,
  Button,
} from "antd";
import {
  BankOutlined,
  DeleteOutlined,
  MenuUnfoldOutlined,
  EditOutlined,
  CheckOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import TenantAccountUpdateForm from "../Forms/TenantAccountUpdateForm";
import moment from "moment";
import RequestHandler from "util/RequestHandler";
import { ColumnHelper, RowHelper } from "util/TableHelper";
import { odata, queryHelper } from "../Filters/Helper";
import TenantAccountTableFilter from "../Filters/TenantAccountTableFilter";
import config from "../../util/ApiforContext";
import CashAccountAddForm from "../Forms/CashAccountAddForm";

const userId = localStorage.getItem("userId");
const BankPage = () => {
  const [tableData, setTableData] = useState({
    data: [],
    total: 0,
    loading: true,
  });

  const [tableDataQueryOptions, setTableDataQueryOptions] = useState(
    queryHelper.getDefaultTableDataQueryOptions()
  );
  const [updateFormData, setUpdateFormData] = useState();
  const [visibleUpdate, setVisibleUpdate] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tableRoles, setTableRoles] = useState({});

  const [addCashAccountVisibility, setAddCashAccountVisibility] =
    useState(false);
  const history = useHistory();

  useEffect(() => {
    setFilterValues();
    getTableData();
  }, [tableDataQueryOptions]);

  useEffect(() => {
    RequestHandler.get(`/user/IsAdmin?userId=${userId}`).then((response) => {
      setIsAdmin(response);
    });
    RequestHandler.get(
      `/TenantAccountParameter/GetTenantAccountParameter`
    ).then((response) => {
      if (response.success) {
        setTableRoles(response.data ?? {});
      }
    });
  }, []);

  const getFilterQuery = () => {
    const { filter } = tableDataQueryOptions;

    const conditions = [];
    let query = "";

    if (filter) {
      conditions.push(filter);
    }

    if (conditions.length !== 0) {
      query = "$filter=" + conditions.join(" and ");
    }
    return query;
  };

  const getTableData = () => {
    setTableData({
      ...tableData,
      loading: true,
    });

    const { skip, take, order } = tableDataQueryOptions;

    RequestHandler.get(
      `/TenantAccount?$count=true&${getFilterQuery()}${order}&$skip=${skip}&$top=${take}`
    ).then((response) => {
      if (Array.isArray(response.value)) {
        setTableData({
          data: response.value,
          total: response["@odata.count"],
          loading: false,
        });
      }
    });
  };

  const setFilterValues = () => {
    const filterValues = {
      ...tableDataQueryOptions.values,
      skip: tableDataQueryOptions.skip,
      take: tableDataQueryOptions.take,
      current: tableDataQueryOptions.current,
      order: tableDataQueryOptions.order,
      filter: tableDataQueryOptions.filter,
    };
    const queryString = queryHelper.getQueryFromValues(filterValues);

    history.replace({
      pathname: window.location.pathname,
      search: queryString,
    });
  };

  const paging = (p, _, sorter) => {
    let orderValue = "";
    if (sorter.columnKey && sorter.order) {
      orderValue =
        "&" +
        (sorter.order === "ascend"
          ? odata.orderBy(sorter.columnKey)
          : odata.orderByDesc(sorter.columnKey));
    }

    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: (p.current - 1) * prevState.take,
        take: p.pageSize,
        current: p.current,
        order: orderValue,
      };
    });
  };

  const onClearTableFilter = () => {
    setTableDataQueryOptions({
      ...tableDataQueryOptions,
      skip: 0,
      current: 1,
      filter: "",
      values: {},
    });
  };

  const onFinishTableFilter = (query, values) => {
    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: 0,
        current: 1,
        filter: query,
        values: values,
      };
    });
  };

  const handleOnClickDelete = (id) => {
    RequestHandler.post(`/TenantAccount/PassiveAccount/${id}`).then(
      (response) => {
        if (response.success) {
          getTableData();
        }
      }
    );
  };

  const showDrawerUpdate = (value) => {
    RequestHandler.get(`/TenantAccount/getbyid/${value}`).then((response) => {
      setUpdateFormData(response);
      setVisibleUpdate(true);
    });
  };

  const onCloseUpdate = () => setVisibleUpdate(false);

  const onAddCashAccountSuccess = () => {
    getTableData();
    setAddCashAccountVisibility(false);
  };

  const onClickExportExcel = () => {
    const hideMessage = message.loading("Excel indiriliyor...", 0);

    RequestHandler.post(
      `/TenantAccount/ExportExcel`,
      {
        query: getFilterQuery(),
      },
      false
    ).then((response) => {
      hideMessage();

      if (response) {
        window.open(
          `${
            config.apiUrl
          }/TenantAccount/DownloadExcel?fileName=${response}&t=${RequestHandler.getToken()}`
        );
      }
    });
  };

  const columns = [
    {
      title: "Banka",
      dataIndex: "bankLogo",
      key: "bankLogo",
      render: (data) => {
        return (
          <div style={{ width: 100 }}>
            <img src={data}></img>
          </div>
        );
      },
    },
    {
      title: "Şube Adı / Kodu",
      dataIndex: "branchName",
      align: "center",
      key: "branchName",
      width: "15%",
      render: (text, row) => (
        <Tooltip placement="topLeft" title={text}>
          <div>
            {row.branchName == null
              ? row.branchCode == null
                ? ""
                : row.branchCode
              : row.branchName}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Hesap Adı",
      dataIndex: "name",
      align: "center",
      key: "name",
      width: "10%",
    },
    {
      title: "Iban / Hesap Numarası",
      dataIndex: "iban",
      align: "center",
      key: "iban",
      width: "15%",
      render: (_, row) => (row.iban ? row.iban : row.accountNumber),
    },
    {
      title: "Döviz",
      dataIndex: "currency",
      align: "center",
      key: "currency",
      width: "10%",
    },
    {
      title: "Bakiye",
      dataIndex: "balanceString",
      key: "balance",
      sorter: true,
      align: "center",
      render: (data) => (
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>{data}</div>
      ),
    },
    tableRoles.isBlockedBalance === true
      ? {
          title: "Blokeli Tutar",
          dataIndex: "blockedAmountString",
          key: "blockedAmount",
          sorter: true,
          align: "center",
          render: (data) => (
            <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              {data}
            </div>
          ),
        }
      : {},
    tableRoles.isAvailableBalance === true
      ? {
          title: "Kullanılabilir Bakiye",
          dataIndex: "availableBalanceString",
          key: "availableBalance",
          sorter: true,
          align: "center",
          render: (data) => (
            <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              {data}
            </div>
          ),
        }
      : {},
    tableRoles.isAvailableCreditBalance === true
      ? {
          title: "Kredili Kullanılabilir Bakiye",
          dataIndex: "availableCreditBalanceString",
          key: "availableCreditBalance",
          sorter: true,
          align: "center",
          render: (data) => (
            <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              {data}
            </div>
          ),
        }
      : {},
    {
      title: "Tip",
      dataIndex: "type",
      align: "center",
      key: "type",
      width: "10%",
      render: (_, row) => {
        if (row.typeValue != null) {
          return <div>{row.type}</div>;
        }
      },
    },
    {
      title: "Son Sorgulanma Tarihi",
      dataIndex: "lastQueryDate",
      key: "lastQueryDate",
      sorter: true,
      align: "center",
      render: (data) =>
        data ? (
          <div>
            {moment(data).format("L")} {moment(data).format("LT")}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Detay",
      dataIndex: "",
      width: "2%",
      align: "center",
      render: (record) => {
        return (
          <Link
            style={{ fontSize: "150%" }}
            to={`/bank-accounts-account-transaction/${record.id}`}
          >
            <MenuUnfoldOutlined />
          </Link>
        );
      },
    },
    {
      title: "Hesaplamaya Dahil Et",
      dataIndex: "isCalculated",
      width: "2%",
      align: "center",
      render: (data) => (
        <div style={{ color: data === true ? "green" : "red" }}>
          <CheckOutlined />
        </div>
      ),
    },
    {
      title: "Düzenle",
      dataIndex: "id",
      width: "1%",
      align: "center",
      render: (data) => (
        <a
          onClick={() => showDrawerUpdate(data)}
          style={{ color: "orange", fontSize: "130%" }}
        >
          <EditOutlined />
        </a>
      ),
    },
    isAdmin === true
      ? {
          title: "Sil",
          dataIndex: "id",
          align: "center",
          render: (data, row) => (
            <Popconfirm
              title="Hesabınız pasife alınacaktır. Aktife almak için bizimle iletişime geçebilirsiniz."
              okText="Tamam, sil"
              cancelText="Hayır silme"
              onConfirm={() => handleOnClickDelete(data)}
              disabled={row.isSystemAccount}
            >
              <a>
                <DeleteOutlined
                  style={{
                    color: row.isSystemAccount ? "gray" : "red",
                    fontSize: "130%",
                  }}
                />
              </a>
            </Popconfirm>
          ),
        }
      : {},
  ];

  const detailColumns = [
    tableRoles.isAvailableBalance === false && {
      title: "Kullanılabilir Bakiye",
      dataIndex: "availableBalanceString",
    },
    {
      title: "Ana Kredi Limiti",
      dataIndex: "creditLimitString",
    },
    {
      title: "Kullanılabilir Kredi Limiti",
      dataIndex: "availableCreditLimitString",
    },
    {
      title: "Harcanan Kredi Bakiyesi",
      dataIndex: "creditBalanceString",
    },
    tableRoles.isBlockedBalance === false && {
      title: "Blokeli Tutar",
      dataIndex: "blockedAmountString",
    },
    tableRoles.isAvailableCreditBalance === false && {
      title: "Kredili Kullanılabilir Bakiye",
      dataIndex: "availableCreditBalanceString",
    },
    {
      title: "Vade Bitiş Tarihi",
      dataIndex: "dueDate",
      render: (data) => (data ? moment(data).format("DD.MM.YYYY") : "-"),
    },
    {
      title: "Vade Faiz Oranı",
      dataIndex: "interestRate",
    },
    {
      title: "Hesap Açılış Tarihi",
      dataIndex: "openDate",
      render: (data) => (data ? moment(data).format("DD.MM.YYYY") : "-"),
    },
  ].filter(Boolean);

  return (
    <>
      <Row>
        <Col xs={24} md={12} lg={16}>
          <p style={{ fontSize: "120%", marginTop: -5 }}>
            <BankOutlined /> HESAP LİSTESİ
          </p>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Row justify="end" align="middle" style={{ paddingRight: 16 }}>
            <Tooltip title="Excel İndir">
              <a
                style={{
                  color: "green",
                  fontSize: "180%",
                  marginRight: 30,
                }}
                onClick={onClickExportExcel}
              >
                <FileExcelOutlined />
              </a>
            </Tooltip>
            <Button
              type="primary"
              onClick={() => {
                setAddCashAccountVisibility(true);
              }}
              style={{ marginBottom: 0 }}
              icon={<PlusOutlined />}
            >
              Kasa Hesabı Ekle
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginBottom: 0 }}
              onClick={() => history.push("/transaction-bank-info")}
            >
              Banka Ekle
            </Button>
          </Row>
        </Col>
      </Row>

      <TenantAccountTableFilter
        data={tableDataQueryOptions.values}
        onClearFilter={onClearTableFilter}
        onFinishFilter={onFinishTableFilter}
        style={{ marginTop: 12, marginBottom: 12 }}
      />

      <Row id="tableArea">
        <Col span={24}>
          <Table
            columns={columns}
            expandable={{
              expandedRowRender: (row) =>
                RowHelper.renderRowDetail({
                  data: row,
                  columns: detailColumns,
                }),
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            style={{ overflowX: "auto", maxHeight: "100%" }}
            rowKey="id"
            filterable={true}
            size="small"
            sortable={true}
            pageable={true}
            onChange={paging}
            pagination={{
              defaultPageSize: tableDataQueryOptions.take,
              pageSizeOptions: ["15", "25", "50", "100"],
              total: tableData.total,
              current: tableDataQueryOptions.current,
            }}
            dataSource={tableData.data}
            loading={tableData.loading}
          />
        </Col>
      </Row>

      <Drawer
        id="addDealerDrawer"
        title="Kasa Hesabı Ekle"
        bodyStyle={{ padding: 24 }}
        width={500}
        onClose={() => setAddCashAccountVisibility(false)}
        visible={addCashAccountVisibility}
      >
        <CashAccountAddForm onSuccess={onAddCashAccountSuccess} />
      </Drawer>

      <Drawer
        title="Banka Hesabı Düzenle"
        width={241}
        onClose={onCloseUpdate}
        visible={visibleUpdate}
        bodyStyle={{ paddingBottom: 80 }}
        footer={<div style={{ textAlign: "right" }}></div>}
      >
        <TenantAccountUpdateForm
          sendDataForUpdate={(val) => {
            if (val === "success") {
              getTableData();
              setVisibleUpdate(false);
            }
          }}
          data={updateFormData}
        />
      </Drawer>
    </>
  );
};

export default BankPage;
